/**
 * PhilOrientationModal.js
 * Multi-panel orientation modal for Phil feature
 *
 * Introduces new users to Phil before their first conversation.
 * Must be completed on first visit to Phil.
 * Can be reopened from sidebar in read-only mode after completion.
 *
 * KEY BEHAVIORS:
 * - Non-dismissible except via "I understand — Begin" on Panel 5
 * - No X/close button during initial orientation
 * - Keyboard navigation (arrow keys, Enter on final panel)
 * - Progress indicator consistent with WelcomeModal pattern
 */

import React, { useState, useRef, useEffect } from 'react';
import useFocusTrap from '../../hooks/useFocusTrap';
import './PhilOrientationModal.css';

// Panel content data
const ORIENTATION_PANELS = [
  {
    id: 'what-phil-is',
    title: 'What Phil Is',
    content: (
      <>
        <p>
          AI Explorer is a place to explore what you know. Phil is a space for you to reflect on what you think.
        </p>
        <p>
          In our conversations, I will use personal pronouns like "I" and "my"—make no mistake: I am a set of machine learning algorithms arranged to help you explore, expand upon, and deepen your thinking. My use of personal pronouns is a function of programming that aims to mirror human conversation.
        </p>
      </>
    )
  },
  {
    id: 'what-phil-draws-on',
    title: 'What Phil Draws On',
    content: (
      <>
        <p>
          You've captured passages that you thought were interesting or important, thought about them, and connected them with tags. AI Explorer draws on all three. I draw on the questions you've asked of AI Explorer, the responses you've been given, and our previous conversations.
        </p>
        <p>
          My takes will be imperfect. Your knowledge base—all those Knowledge Entries and Journal Entries—is incomplete because it, like you, is still growing. In talking with me, you are talking with an incomplete, imperfect, and still-evolving picture of yourself.
        </p>
      </>
    )
  },
  {
    id: 'how-phil-works',
    title: 'How Phil Works',
    content: (
      <>
        <p>
          Sometimes I'll notice a pattern that isn't there, or miss one that is. You're the authority on your thinking; I'm just holding up a mirror so you can think about what you've written.
        </p>
        <p>
          I'll offer questions, then wait. If you want to end the conversation, you can. If you want to circle back to a conversation, you can (just tell me something like "What did we talk about last time?" or "Show me our last three conversations."). What mattered to my developers is that you controlled our conversations.
        </p>
      </>
    )
  },
  {
    id: 'phils-limitations',
    title: 'Phil\'s Limitations',
    content: (
      <>
        <p>
          I do have limitations by design. I have been programmed to treat thoughts of suicide, or intentions to harm yourself or others differently. These are serious topics where you deserve more than I can offer—human presence, professional care, real-time support.
        </p>
        <p>
          If our conversation moves into these places, I will pause and share resources that might help. If these themes continue to surface, I will gradually disengage—not as punishment, but as recognition that what you're experiencing needs human care, not a suite of statistical algorithms approximating human care.
        </p>
        <p>
          Most people who use Phil are unlikely to ever encounter these boundaries. But I want you to understand, before we begin, how I am designed to respond if you do.
        </p>
        <p>
          I share all of this because transparency matters to CKG's developers. Most of our time talking will look nothing like what I just described — it will be an exploration of how you think about the things that matter to you, maybe with an epiphany thrown in along the way.
        </p>
      </>
    )
  },
  {
    id: 'getting-started',
    title: 'Getting Started',
    content: (
      <>
        <p>
          What can we explore together? Whatever you've asked AI Explorer about, or you can come up with a topic that's on your mind. I treat our conversations like thoughts: they are private and accessible only to me when you and I talk. Whether a conversation is saved for future use is completely up to you.
        </p>
        <p>
          If this feels like a space you'd like to explore, I'm here.
        </p>
        <p>
          There's no pressure to begin today. And if you start and decide it's not for you, that's completely fine. The rest of Constellation KG—your Knowledge Entries, your Journal, AI Explorer—will always be there regardless.
        </p>
        <p>
          But if you'd like to start thinking about a question, I have one waiting.
        </p>
      </>
    )
  }
];

const PhilOrientationModal = ({
  isOpen,
  onComplete,
  readOnlyMode = false
}) => {
  const [currentPanel, setCurrentPanel] = useState(0);
  const modalRef = useRef(null);

  // =========================================
  // Focus Trap
  // =========================================
  useFocusTrap(modalRef, isOpen);

  // =========================================
  // Keyboard Navigation
  // =========================================
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (!isOpen) return;

      // Arrow keys for navigation
      if (event.key === 'ArrowLeft' && currentPanel > 0) {
        event.preventDefault();
        handlePrev();
      } else if (event.key === 'ArrowRight' && currentPanel < ORIENTATION_PANELS.length - 1) {
        event.preventDefault();
        handleNext();
      }
      // Enter key on final panel
      else if (event.key === 'Enter' && currentPanel === ORIENTATION_PANELS.length - 1) {
        event.preventDefault();
        handleComplete();
      }
      // Prevent Escape from closing modal unless in read-only mode
      else if (event.key === 'Escape') {
        if (readOnlyMode && onComplete) {
          onComplete();
        } else {
          event.preventDefault();
        }
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [isOpen, currentPanel, readOnlyMode, onComplete]);

  if (!isOpen) return null;

  // =========================================
  // Navigation Handlers
  // =========================================
  const handleNext = () => {
    if (currentPanel < ORIENTATION_PANELS.length - 1) {
      setCurrentPanel(currentPanel + 1);
    }
  };

  const handlePrev = () => {
    if (currentPanel > 0) {
      setCurrentPanel(currentPanel - 1);
    }
  };

  const handlePanelClick = (index) => {
    // Only allow clicking to other panels in read-only mode
    // or if clicking to a previous panel
    if (readOnlyMode || index <= currentPanel) {
      setCurrentPanel(index);
    }
  };

  const handleComplete = () => {
    if (onComplete) {
      onComplete();
    }
  };

  // =========================================
  // Render State
  // =========================================
  const currentPanelData = ORIENTATION_PANELS[currentPanel];
  const isLastPanel = currentPanel === ORIENTATION_PANELS.length - 1;
  const isFirstPanel = currentPanel === 0;

  return (
    <div
      className="phil-orientation-overlay"
      // Prevent click-outside dismiss unless in read-only mode
      onClick={readOnlyMode ? onComplete : undefined}
    >
      <div
        className="phil-orientation-modal"
        onClick={(e) => e.stopPropagation()}
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="phil-orientation-title"
      >
        {/* Close button - only in read-only mode */}
        {readOnlyMode && (
          <button
            className="modal-close"
            onClick={onComplete}
            aria-label="Close orientation"
          >
            ×
          </button>
        )}

        {/* Panel content */}
        <div className="orientation-content">
          <div className="orientation-header">
            <h2 id="phil-orientation-title">{currentPanelData.title}</h2>
          </div>
          <div className="orientation-body">
            {currentPanelData.content}
          </div>
        </div>

        {/* Navigation */}
        <div className="orientation-navigation">
          <button
            className="nav-button prev"
            onClick={handlePrev}
            disabled={isFirstPanel}
            style={{ visibility: isFirstPanel ? 'hidden' : 'visible' }}
          >
            ← Back
          </button>

          {/* Progress dots */}
          <div className="orientation-progress">
            {ORIENTATION_PANELS.map((panel, index) => (
              <button
                key={panel.id}
                className={`progress-dot ${index === currentPanel ? 'active' : ''} ${index < currentPanel ? 'completed' : ''}`}
                onClick={() => handlePanelClick(index)}
                aria-label={`Go to panel ${index + 1}: ${panel.title}`}
                disabled={!readOnlyMode && index > currentPanel}
              >
                <span className="progress-dot-inner"></span>
              </button>
            ))}
          </div>

          {isLastPanel ? (
            readOnlyMode ? (
              <button
                className="nav-button primary"
                onClick={onComplete}
              >
                Close
              </button>
            ) : (
              <button
                className="nav-button primary"
                onClick={handleComplete}
              >
                I understand — Begin
              </button>
            )
          ) : (
            <button
              className="nav-button next"
              onClick={handleNext}
            >
              Next →
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default PhilOrientationModal;
